import { useEffect, useState } from 'react'

import { useIncidentCreationData } from '../hooks/useIncidentCreationData'
import { UseFormMethods } from 'react-hook-form'

import { Box, FormLabel } from '@chakra-ui/react'

import {
  AddressSearch,
  FormInputSelectControl,
  StyledFormInputControl,
} from '@/components/ui'
import { Facility } from '@/graphql/generated/schemas'
import { getDeviceOptions, getFloorOptions } from '@/utils/forms/optionBuilders'

import { FormInputsIProps } from '../types/types'
import { isDeviceHealthSelected } from '../utils/utils'

interface IncidentModalBodyIProps {
  form: UseFormMethods<FormInputsIProps>
  facility: Facility
}

export const NormalIncidentForm = ({
  form,
  facility,
}: IncidentModalBodyIProps) => {
  const { watch, control, errors, reset, getValues } = form
  const [floorId, setFloorId] = useState<string | null>(null)
  const [incidentCategoryId, setIncidentCategoryId] = useState<string | null>(
    null
  )

  const { isFloorsLoading, floorsData, devicesData, categoriesData } =
    useIncidentCreationData({ facilityId: facility?.id, floorId })

  const floorOptions = getFloorOptions(floorsData)
  const deviceOptions = getDeviceOptions(devicesData)

  const watchIncidentCategoryId = watch('incidentCategory')
  const watchFloorOrigin = watch('floorOrigin')

  const isDeviceHealth = isDeviceHealthSelected(
    categoriesData,
    incidentCategoryId
  )

  useEffect(() => {
    if (watchIncidentCategoryId) {
      setIncidentCategoryId(watchIncidentCategoryId.value ?? null)
    }
  }, [watchIncidentCategoryId])

  useEffect(() => {
    if (watchFloorOrigin) {
      setFloorId(watchFloorOrigin.value)
      reset({
        ...getValues(),
        deviceOrigin: null,
      })
    }
  }, [watchFloorOrigin])

  // TODO - Re-add this, but make it so it won't set a Site Snapshot
  // useEffect(() => {
  //   // If there is only one floor, default to select it
  //   if (floorsData && floorOptions?.length === 1) {
  //     setValue('floorOrigin', floorOptions[0])
  //   }
  // }, [floorsData])

  return (
    <Box p={0}>
      <Box mb={3}>
        <FormInputSelectControl
          control={control}
          dataTestId='createIncidentModal_floorName'
          defaultValue={null}
          id='floorOrigin'
          isClearable
          isInvalid={!!errors.floorOrigin}
          isLoading={isFloorsLoading}
          label={isDeviceHealth ? 'Floor Name *' : 'Floor Name (optional)'}
          noOptionsMessage={() =>
            !facility?.id ? 'Please select a facility first' : 'No floors'
          }
          options={floorOptions}
          placeholder='Select the Floor of Origin'
          rules={{
            required: isDeviceHealth
              ? 'Floor Name is required for Device Health incidents'
              : false,
          }}
        />
      </Box>
      {facility?.address && (
        <StyledFormInputControl mb='4'>
          <FormLabel>Address</FormLabel>
          <AddressSearch
            defaultValue={facility?.address?.[0]}
            disabled={true}
            showTooltip={false}
          />
        </StyledFormInputControl>
      )}
      {isDeviceHealth && (
        <Box mb={3}>
          <FormInputSelectControl
            control={control}
            defaultValue={null}
            id='deviceOrigin'
            isInvalid={!!errors?.deviceOrigin}
            label='Device Name *'
            options={deviceOptions}
            placeholder='Select the Device of Origin'
            rules={{
              validate: (value) => {
                const currentCategory = getValues('incidentCategory')
                return !(currentCategory?.label === 'Device Health' && !value)
              },
            }}
          />
        </Box>
      )}
    </Box>
  )
}
